import { useRef } from 'react';
import { FileText, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, API_BASE as API } from '../../config';
import { isImageName } from './entryHelpers';

/** Pictures preview carousel + upload trigger. */
export default function EntryPicturesPanel({
  claim, pictures, setPictures, picIndex, setPicIndex, uploading, setUploading,
}) {
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !claim?.report_no) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('report_no', claim.report_no);
      for (const f of files) fd.append('files', f);
      const res = await axios.post(`${API}/claim-docs/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPictures(res.data.uploaded || []);
      setPicIndex(Math.max(0, (res.data.uploaded || []).length - files.length));
      toast.success(`${files.length} file(s) uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeletePicture = async (fileKey) => {
    try {
      await axios.delete(
        `${API}/claim-docs/files?report_no=${encodeURIComponent(claim.report_no)}&file_key=${encodeURIComponent(fileKey)}`,
        { withCredentials: true },
      );
      const updated = pictures.filter((p) => p.key !== fileKey);
      setPictures(updated);
      setPicIndex(Math.min(picIndex, Math.max(0, updated.length - 1)));
      toast.success('File deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="border-t pt-2">
      <label className="text-gray-500 text-sm block mb-2">Pictures</label>
      <div className="border border-gray-200 rounded p-3 mb-2 bg-white">
        {pictures.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <a
                href={`${BACKEND_URL}${pictures[picIndex]?.url}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-blue-600 underline truncate max-w-[60%]"
                data-testid="pic-filename"
              >
                {pictures[picIndex]?.name}
              </a>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {pictures[picIndex]?.size ? `${(pictures[picIndex].size / 1024 / 1024).toFixed(1)} MB` : ''}
                </span>
                <button
                  onClick={() => handleDeletePicture(pictures[picIndex]?.key)}
                  className="text-gray-400 hover:text-red-500"
                  data-testid="pic-delete-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setPicIndex(Math.max(0, picIndex - 1))}
                disabled={picIndex === 0}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                data-testid="pic-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex-1 flex justify-center">
                {isImageName(pictures[picIndex]?.name) ? (
                  <img
                    src={`${BACKEND_URL}${pictures[picIndex]?.url}`}
                    alt={pictures[picIndex]?.name}
                    className="max-h-40 rounded object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex items-center justify-center h-40 bg-gray-50 rounded w-full">
                    <FileText className="w-10 h-10 text-gray-400" />
                  </div>
                )}
              </div>
              <button
                onClick={() => setPicIndex(Math.min(pictures.length - 1, picIndex + 1))}
                disabled={picIndex >= pictures.length - 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                data-testid="pic-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">File {picIndex + 1} of {pictures.length}</p>
          </>
        ) : (
          <div className="flex items-center justify-center h-32 bg-gray-50 rounded">
            <p className="text-sm text-gray-400 italic">No pictures uploaded</p>
          </div>
        )}
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        multiple
        className="hidden"
        accept="image/*,.pdf,.doc,.docx"
        data-testid="pic-file-input"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full border-2 border-dashed border-gray-300 rounded p-4 text-center hover:border-blue-400 transition-colors cursor-pointer"
        data-testid="pic-upload-btn"
      >
        <span className="text-sm text-blue-600 underline">
          {uploading ? 'Uploading...' : 'Click to select a file'}
        </span>
      </button>
    </div>
  );
}
